import { TFunction } from 'i18next'
import draftToHtml from 'draftjs-to-html'
import type { RawDraftContentState } from 'draft-js'
import type { DraftJsContent } from '../../../../../pure-js/libs/Common'
import { StrengthTextLevel, WeaknessTextLevel } from '../../../../../pure-js/enums/FeedbackLevel'

/**
 * Check if a value is a DraftJsContent object
 */
const isDraftJsContent = (value: unknown): value is DraftJsContent => {
  return (
    typeof value === 'object' && value !== null && 'blocks' in value && Array.isArray((value as DraftJsContent).blocks)
  )
}

/**
 * Check if DraftJsContent has any actual text content
 */
const hasDraftJsTextContent = (content: DraftJsContent): boolean => {
  return content.blocks.some((block) => block.text && block.text.trim().length > 0)
}

/**
 * Convert DraftJsContent to HTML string for display
 */
const draftJsToHtml = (content: DraftJsContent): string => {
  try {
    // Cast to RawDraftContentState for draftToHtml compatibility
    return draftToHtml(content as unknown as RawDraftContentState)
  } catch {
    // Fallback: extract plain text from blocks
    return content.blocks.map((block) => block.text).join('\n')
  }
}

/**
 * Helper types for feedback text extraction
 */
interface QuestionData {
  questions: Record<string, number>
}

interface BooleanPlanData {
  teamMadePlan?: boolean
  teamFollowedPlan?: boolean
  teamEvaluatedPlan?: boolean
}

interface PerformanceData {
  uridiumPer15Min: {
    current: number
    previous: number | null
  }
}

interface HypothermiaData {
  teamTimeParalyzed: {
    current: number
    previous: number | null
  }
}

interface CommunicationLoopData {
  loops: Array<{
    players?: Array<{
      talkedTotal?: number
    }>
  }>
}

interface EmotionData {
  dominantEmotionClass?: string
  teamCommunicatedEmotionClass?: string
}

interface InclusionData {
  teamTotalInvitations: number
}

interface ConstructiveDebateData {
  disagreeRatio: number
}

interface ImprovementAreaData {
  variable: string
  recommendationDirection: 'increase' | 'decrease' | 'balance'
  /** Which text describes it, from the answer. */
  textLevel?: StrengthTextLevel | WeaknessTextLevel
  context?: 'strengths' | 'weaknesses'
}

/** How a text key ends for each direction; balance has no text of its own and reads as an increase. */
const TEXT_KEY_DIRECTION_SUFFIXES: Partial<Record<ImprovementAreaData['recommendationDirection'], string>> = {
  increase: 'Increase',
  decrease: 'Decrease'
}
const DEFAULT_TEXT_KEY_DIRECTION_SUFFIX = 'Increase'

const STRENGTH_TEXT_LEVELS = Object.values(StrengthTextLevel)
const WEAKNESS_TEXT_LEVELS = Object.values(WeaknessTextLevel)

// Without a level of its own a strength reads as good and a weakness as an idea.
const readStrengthTextLevel = (textLevel?: string): StrengthTextLevel =>
  STRENGTH_TEXT_LEVELS.find((level) => level === textLevel) ?? StrengthTextLevel.GOOD

const readWeaknessTextLevel = (textLevel?: string): WeaknessTextLevel =>
  WEAKNESS_TEXT_LEVELS.find((level) => level === textLevel) ?? WeaknessTextLevel.IDEA

/** An outstanding strength has one text whatever its direction; the others have one per direction. */
function buildStrengthTextKey(keyBase: string, textLevel: StrengthTextLevel, directionSuffix: string): string {
  if (textLevel === StrengthTextLevel.OUTSTANDING) return `${keyBase}.${StrengthTextLevel.OUTSTANDING}`

  return `${keyBase}.${textLevel}${directionSuffix}`
}

/**
 * Question key mapping for psychological safety questions
 */
const TEAM_REFLECTION_KEYS = [
  'teamReflectionQuestionOne',
  'teamReflectionQuestionTwo',
  'teamReflectionQuestionThree',
  'teamReflectionQuestionFour',
  'teamReflectionQuestionFive',
  'teamReflectionQuestionSix',
  'teamReflectionQuestionSeven',
  'teamReflectionQuestionEight',
  'teamReflectionQuestionNine',
  'teamReflectionQuestionTen',
  'teamReflectionQuestionEleven'
] as const

const TEAM_REFLECTION_LABEL_LOOKUP: Record<string, string> = TEAM_REFLECTION_KEYS.reduce<Record<string, string>>(
  (acc, key) => {
    acc[key] = key
    return acc
  },
  {}
)

/**
 * Extract feedback text for individual emotion data
 */
function extractEmotionFeedback(data: EmotionData, t: TFunction): string {
  if (!data.dominantEmotionClass) return ''

  return t(`gamer.feedback.sections.experience.emotions.${data.dominantEmotionClass}`, {
    defaultValue: t('gamer.feedback.sections.experience.emotions.fallback', 'Your emotional state during this session.')
  })
}

/**
 * Extract feedback text for individual psychological safety questions
 */
function extractIndividualPsychSafetyFeedback(data: QuestionData, t: TFunction): string {
  const questionEntries = Object.entries(data.questions) as [string, number][]
  const sorted = questionEntries.sort((a, b) => b[1] - a[1])
  const highest = sorted[0]
  const lowest = sorted[sorted.length - 1]

  const highestLabel = t(`gamer.feedback.sections.experience.psychologicalSafety.questions.${highest[0]}`, {
    defaultValue: highest[0]
  })
  const lowestLabel = t(`gamer.feedback.sections.experience.psychologicalSafety.questions.${lowest[0]}`, {
    defaultValue: lowest[0]
  })

  return t('gamer.feedback.sections.experience.psychologicalSafety.description', {
    highestKey: highestLabel,
    lowestKey: lowestLabel,
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for planning/executing/evaluating sections
 */
function extractPlanningFeedback(data: BooleanPlanData, activeTab: string, t: TFunction): string {
  const booleanValueMap: Record<string, boolean | undefined> = {
    plan: data.teamMadePlan,
    follow: data.teamFollowedPlan,
    evaluate: data.teamEvaluatedPlan
  }

  const booleanValue = booleanValueMap[activeTab]
  if (booleanValue === undefined) return ''

  const boolKey = booleanValue ? 'true' : 'false'
  return t(`gamer.feedback.sections.planningExecutingEvaluating.${activeTab}.${boolKey}`, {
    ns: 'feedbackTexts',
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for team psychological safety questions
 */
function extractTeamSafetyQuestionsFeedback(data: QuestionData, t: TFunction): string {
  const questionEntries = Object.entries(data.questions) as [string, number][]
  const sorted = questionEntries.sort((a, b) => b[1] - a[1])
  const highest = sorted[0]
  const lowest = sorted[sorted.length - 1]

  const highestLabel = t(
    `gamer.feedback.psychologicalSafety.${TEAM_REFLECTION_LABEL_LOOKUP[highest[0]] || highest[0]}`,
    {
      defaultValue: highest[0]
    }
  )
  const lowestLabel = t(`gamer.feedback.psychologicalSafety.${TEAM_REFLECTION_LABEL_LOOKUP[lowest[0]] || lowest[0]}`, {
    defaultValue: lowest[0]
  })

  return t('gamer.feedback.sections.teamPsychologicalSafety.safetyQuestions.text', {
    ns: 'feedbackTexts',
    highestKey: highestLabel,
    lowestKey: lowestLabel,
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for constructive debate
 */
function extractConstructiveDebateFeedback(data: ConstructiveDebateData, t: TFunction): string {
  const disagreeRatioPct = Math.round(data.disagreeRatio * 100)
  return t('gamer.feedback.sections.teamPsychologicalSafety.constructiveDebate.text', {
    ns: 'feedbackTexts',
    disagreeRatioPct: `${disagreeRatioPct}%`,
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for performance metrics
 */
function extractPerformanceFeedback(data: PerformanceData, t: TFunction): string {
  const { current, previous } = data.uridiumPer15Min
  const delta = Math.abs(current - (previous || 0))

  let conditionKey = ''
  if (current === 0) {
    conditionKey = 'currentZero'
  } else if (previous === null || previous === undefined) {
    conditionKey = 'noBaseline'
  } else if (current > previous) {
    conditionKey = 'increased'
  } else if (current === previous) {
    conditionKey = 'same'
  } else {
    conditionKey = 'decreased'
  }

  return t(`gamer.feedback.sections.taskPerformance.performance.${conditionKey}`, {
    ns: 'feedbackTexts',
    current,
    previous: previous || 0,
    delta,
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for hypothermia metrics
 */
function extractHypothermiaFeedback(data: HypothermiaData, t: TFunction): string {
  const { current, previous } = data.teamTimeParalyzed
  const delta = Math.abs(current - (previous || 0))

  let conditionKey = ''
  if (previous === 0) {
    conditionKey = 'previousZero'
  } else if (previous === null || previous === undefined) {
    conditionKey = 'noBaseline'
  } else if (current > previous) {
    conditionKey = 'increased'
  } else if (current === previous) {
    conditionKey = 'same'
  } else {
    conditionKey = 'decreased'
  }

  return t(`gamer.feedback.sections.taskPerformance.hypothermia.${conditionKey}`, {
    ns: 'feedbackTexts',
    current,
    previous: previous || 0,
    delta,
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for communication balance
 */
function extractCommunicationBalanceFeedback(data: CommunicationLoopData, t: TFunction): string {
  const allTalkedValues: number[] = []

  data.loops.forEach((loop) => {
    if (loop.players && Array.isArray(loop.players)) {
      loop.players.forEach((player) => {
        if (typeof player.talkedTotal === 'number') {
          allTalkedValues.push(player.talkedTotal)
        }
      })
    }
  })

  const highestTalkerCount = allTalkedValues.length > 0 ? Math.max(...allTalkedValues) : 0
  const lowestTalkerCount = allTalkedValues.length > 0 ? Math.min(...allTalkedValues) : 0

  return t('gamer.feedback.sections.communicationClimate.balance.text', {
    ns: 'feedbackTexts',
    highestTalkerCount,
    lowestTalkerCount,
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for team emotions
 */
function extractTeamEmotionsFeedback(emotionClass: string, t: TFunction): string {
  const emotion = emotionClass.toLowerCase()
  return t(`gamer.feedback.sections.communicationClimate.emotions.${emotion}`, {
    ns: 'feedbackTexts',
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for inclusion metrics
 */
function extractInclusionFeedback(data: InclusionData, t: TFunction): string {
  return t('gamer.feedback.sections.communicationClimate.inclusion.text', {
    ns: 'feedbackTexts',
    teamTotalInvitations: data.teamTotalInvitations,
    defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
  })
}

/**
 * Extract feedback text for improvement areas and strengths
 */
function extractImprovementAreaFeedback(
  data: ImprovementAreaData,
  titleKey: string,
  variant: 'team' | 'individual',
  t: TFunction
): string {
  const { variable: variableName, recommendationDirection, textLevel } = data

  const isStrength = titleKey.includes('strengths')
  const context = isStrength ? 'strengths' : 'weaknesses'

  // Determine prefix based on variant
  const prefix = variant === 'individual' ? 'player' : 'team'

  const keyBase = `${prefix}.${context}.${variableName}`
  const directionSuffix = TEXT_KEY_DIRECTION_SUFFIXES[recommendationDirection] ?? DEFAULT_TEXT_KEY_DIRECTION_SUFFIX

  const textKey = isStrength
    ? buildStrengthTextKey(keyBase, readStrengthTextLevel(textLevel), directionSuffix)
    : `${keyBase}.${readWeaknessTextLevel(textLevel)}${directionSuffix}`

  const feedbackText = t(textKey, {
    ns: 'feedbackTexts',
    defaultValue: `[Missing: ${variableName}]`
  })

  // Store context in data for VisualizationRenderer
  data.context = context

  return feedbackText
}

/**
 * Main function to extract feedback text based on active tab data
 *
 * IMPORTANT: For report data, llmAnalysis must be checked FIRST before other conditions
 * that might match (like loops, disagreeRatio, questions), otherwise feedback text
 * will be returned instead of the report's llmAnalysis.
 */
export function extractFeedbackText(
  activeTabData: any,
  activeTab: string,
  variant: 'team' | 'individual',
  titleKey: string,
  t: TFunction
): string {
  // PRIORITY 1: Check for llmAnalysis FIRST (used in report data)
  // This must come before other checks that might also match report data structures
  // (e.g., loops, disagreeRatio, questions) to ensure report text is shown
  const isExerciseAndReflect = activeTab.includes('exerciseAndReflect') || activeTab.includes('ExerciseAndReflect')

  if (activeTabData.llmAnalysis) {
    let extractedText = ''
    let hasTextContent = false

    // Handle DraftJsContent format (new format from backend)
    if (isDraftJsContent(activeTabData.llmAnalysis)) {
      // Check if there's actual text content before converting to HTML
      hasTextContent = hasDraftJsTextContent(activeTabData.llmAnalysis)
      if (hasTextContent) {
        extractedText = draftJsToHtml(activeTabData.llmAnalysis)
      }
    }
    // Handle legacy string format
    else if (typeof activeTabData.llmAnalysis === 'string') {
      extractedText = activeTabData.llmAnalysis
      hasTextContent = extractedText.trim().length > 0
    }

    // For exerciseAndReflect tabs, show placeholder if text is empty
    if (isExerciseAndReflect && (!hasTextContent || !extractedText || extractedText.trim().length === 0)) {
      return t('facilitator.reportFlow.sections.exerciseAndReflect.placeholder', 'No pre-generate suggestions')
    }

    // Return extracted text if it has content
    if (hasTextContent && extractedText && extractedText.trim().length > 0) {
      return extractedText
    }

    // If llmAnalysis exists but we couldn't extract text and it's not exerciseAndReflect, continue to other priorities
  }

  // If this is exerciseAndReflect tab but no llmAnalysis exists at all, show placeholder
  if (isExerciseAndReflect && !activeTabData.llmAnalysis) {
    return t('facilitator.reportFlow.sections.exerciseAndReflect.placeholder', 'No pre-generate suggestions')
  }

  // PRIORITY 2: Individual emotion data (only for individual variant, doesn't have llmAnalysis)
  if (activeTabData.dominantEmotionClass) {
    return extractEmotionFeedback(activeTabData, t)
  }

  // PRIORITY 3: Individual psychological safety questions (only for individual variant)
  if (activeTab === 'psychologicalSafety' && activeTabData.questions && variant === 'individual') {
    return extractIndividualPsychSafetyFeedback(activeTabData, t)
  }

  // PRIORITY 4: Planning/Executing/Evaluating sections (boolean plan data, doesn't have llmAnalysis)
  if (
    activeTabData.teamMadePlan !== undefined ||
    activeTabData.teamFollowedPlan !== undefined ||
    activeTabData.teamEvaluatedPlan !== undefined
  ) {
    return extractPlanningFeedback(activeTabData, activeTab, t)
  }

  // PRIORITY 5: Team safety questions (only if no llmAnalysis - checked above)
  if (activeTabData.questions && typeof activeTabData.questions === 'object') {
    return extractTeamSafetyQuestionsFeedback(activeTabData, t)
  }

  // PRIORITY 6: Team health (only if no llmAnalysis - checked above)
  // Report flow uses tab keys like `psychologicalSafetyReport_teamHealth` and `variable: 'teamHealth'`.
  const isTeamHealthTab =
    activeTab === 'teamHealth' || activeTab.endsWith('_teamHealth') || activeTabData.variable === 'teamHealth'
  const isLegacyTeamHealthPayload = activeTabData.teamHealth !== undefined
  if (isTeamHealthTab && (isLegacyTeamHealthPayload || activeTabData.variable === 'teamHealth')) {
    return t('gamer.feedback.sections.teamPsychologicalSafety.teamHealth.text', {
      ns: 'feedbackTexts',
      defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
    })
  }

  // PRIORITY 7: Constructive debate (only if no llmAnalysis - checked above)
  if (activeTabData.disagreeRatio !== undefined) {
    return extractConstructiveDebateFeedback(activeTabData, t)
  }

  // PRIORITY 8: Performance metrics (doesn't have llmAnalysis)
  if (activeTabData.uridiumPer15Min) {
    return extractPerformanceFeedback(activeTabData, t)
  }

  // PRIORITY 9: Hypothermia metrics (doesn't have llmAnalysis)
  if (activeTabData.teamTimeParalyzed) {
    return extractHypothermiaFeedback(activeTabData, t)
  }

  // PRIORITY 10: Drone usage (doesn't have llmAnalysis)
  if (activeTabData.hasUnlockedDrone !== undefined) {
    return t('gamer.feedback.sections.taskPerformance.droneUsage.text', {
      ns: 'feedbackTexts',
      defaultValue: t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
    })
  }

  // PRIORITY 11: Communication balance (only if no llmAnalysis - checked above)
  // Note: Report data has loops AND llmAnalysis, so llmAnalysis check above handles it
  if (activeTabData.loops && Array.isArray(activeTabData.loops)) {
    return extractCommunicationBalanceFeedback(activeTabData, t)
  }

  // PRIORITY 12: Team emotions (only if no llmAnalysis - checked above)
  if (activeTabData.teamCommunicatedEmotionClass) {
    return extractTeamEmotionsFeedback(activeTabData.teamCommunicatedEmotionClass, t)
  }

  // PRIORITY 13: Inclusion metrics (doesn't have llmAnalysis)
  if (activeTabData.teamTotalInvitations !== undefined) {
    return extractInclusionFeedback(activeTabData, t)
  }

  // PRIORITY 14: Improvement areas and strengths (only if no llmAnalysis - checked above)
  if (activeTabData.variable && activeTabData.normalizedScore !== undefined && activeTabData.recommendationDirection) {
    return extractImprovementAreaFeedback(activeTabData, titleKey, variant, t)
  }

  // Default fallback
  return t('gamer.feedback.fallback.moreDataRequired', 'More data analysis is required')
}
